import { useCallback } from 'react';
import { IndexDB } from '../../../utils/indexDB';
import logger from '../../../utils/logger';
import { calculateMixDesign } from './mix-design-utils';
import { useStores } from '../../../hooks/use-stores';
import RootStore from '../../../stores/root-store';
import {
  BatchValues,
  MixDesignItem,
} from '../../../../types/batch/BatchValues';
import { DEFAULT_QUANTITY_TO_BE_BATCHED } from '../../../constants/BatchDefaults';

const RECENT_BATCHES_KEY = 'batchFormRecentValues';
const DEFAULT_RETENTION_MINUTES = 30;

let indexDB: IndexDB | null = null;
let isInitialized = false;

export interface RecentSavedBatch {
  id: string;
  timestamp: number;
  batchCode: string;
  mixDescription?: string | null;
  orderCode?: string | null;
  ticketId?: string | null;
  truckId?: string | null;
  truckCode?: string | null;
  values: any;
}

interface ApplyBatchOptions {
  excludeWater?: boolean;
  excludeQuantity?: boolean;
}

const initializeIndexDB = async (): Promise<void> => {
  if (isInitialized) {
    return;
  }

  try {
    indexDB = new IndexDB();
    await indexDB.init();
    isInitialized = true;
    logger.log('PreviousValuesManager initialized');
  } catch (err) {
    logger.warn('Failed to initialize PreviousValuesManager:', err);
    throw err;
  }
};

const getRetentionMinutes = (settingsStore: RootStore['settingsStore']): number => {
  if (process.env.NODE_ENV === 'development') {
    return 60 * 24 * 2; // 48 hours retention in development
  }
  const settingValue = settingsStore?.configuration?.maximumDurationForDryUp as unknown as number;
  return settingValue || DEFAULT_RETENTION_MINUTES;
};

const cleanupExpiredBatches = async (
  batches: RecentSavedBatch[],
  settingsStore: RootStore['settingsStore'],
): Promise<RecentSavedBatch[]> => {
  const retentionMinutes = getRetentionMinutes(settingsStore);
  const now = Date.now();
  const retentionMs = retentionMinutes * 60 * 1000;
  return batches.filter(batch => (now - batch.timestamp) < retentionMs);
};

export const savePreviousValues = async (
  values: BatchValues,
  settingsStore: RootStore['settingsStore'],
): Promise<boolean> => {
  await initializeIndexDB();

  try {
    const valuesToSave: Partial<BatchValues> = {
      ...values,
      // Purposefully omit some values
      id: undefined,
      code: undefined,
      dispatchOrderId: undefined,
      dispatchOrderCode: undefined,
      order: undefined,
      orderedBy: undefined,
      ticket: undefined,
      batchOrderId: undefined,
      batchOrderCode: undefined,
    };

    // Save to the recent batches list with timestamp
    const recentBatches = await loadRecentBatches(settingsStore);
    const newBatch: RecentSavedBatch = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      batchCode: values.code || `Batch ${new Date().toLocaleTimeString()}`,
      // Save ticket and truck info for display
      mixDescription: values.mixDescription || null,
      ticketId: values.ticket?.id || null,
      orderCode: values.order?.code || null,
      truckId: values.truckId || null,
      truckCode: values.truckCode || null,
      values: valuesToSave,
    };

    const updatedBatches = await cleanupExpiredBatches([newBatch, ...recentBatches], settingsStore);

    // Keep only the most recent 20 batches even after cleanup
    const batchesToSave = updatedBatches.slice(0, 20);

    await indexDB!.save(RECENT_BATCHES_KEY, batchesToSave);
    logger.log('Saved previous batch form values');
    return true;
  } catch (err) {
    logger.warn('Failed to save previous batch form values:', err);
    return false;
  }
};

export const checkPreviousValuesExist = async (settingsStore: RootStore['settingsStore']): Promise<boolean> => {
  const mostRecentBatch = await getMostRecentBatch(settingsStore);
  return !!mostRecentBatch;
};

export const useApplyBatchToForm = () => {
  const {
    materialStore,
    batchStore,
  } = useStores();

  const applyBatchToForm = useCallback(
    (
      batch: RecentSavedBatch,
      currentValues: BatchValues,
      setValues: (values: any) => void,
      options: ApplyBatchOptions = {},
    ): any => {
      const {
        excludeWater = false,
        excludeQuantity = false,
      } = options;

      let batchValues = batch.values;

      // If excludeWater is true, filter out water materials from mixDesign
      if (excludeWater && batchValues.mixDesign) {
        batchValues = {
          ...batchValues,
          mixDesign: batchValues.mixDesign.filter((item: MixDesignItem) => {
            // Keep non-water materials
            return !materialStore.isWater(item.constituentItemId) &&
                   !materialStore.isSecondWater(item.constituentItemId);
          }),
        };
      }

      if (excludeQuantity) {
        batchValues = {
          ...batchValues,
          quantityToBeBatched: DEFAULT_QUANTITY_TO_BE_BATCHED,
          batchSize: DEFAULT_QUANTITY_TO_BE_BATCHED,
          dropSize: DEFAULT_QUANTITY_TO_BE_BATCHED,
        };
      }

      const mergedValues: BatchValues = {
        ...currentValues,
        ...batchValues,
        code: currentValues.code,
        id: currentValues.id,
        order: currentValues.order,
      };

      const calculatedValues = calculateMixDesign(mergedValues, batchStore.rootStore);

      setValues(calculatedValues);

      const message = excludeWater
        ? `Dry materials from "${batch.batchCode}" loaded successfully`
        : `Values from "${batch.batchCode}" loaded successfully`;

      batchStore.showInfo(message);
      logger.log('Applied batch form values from:', batch.batchCode, excludeWater ? '(dry load)' : '');

      return calculatedValues;
    },
    [batchStore, materialStore],
  );

  return applyBatchToForm;
};

export const useApplyPreviousValues = () => {
  const applyBatchToForm = useApplyBatchToForm();
  const { batchStore, settingsStore } = useStores();

  const applyPreviousValues = useCallback(
    async (currentValues: BatchValues, setValues: (values: any) => void): Promise<any | false> => {
      const mostRecentBatch = await getMostRecentBatch(settingsStore);

      if (!mostRecentBatch) {
        batchStore.showWarning('No previous values found. Submit a batch first to save values.');
        return false;
      }

      return applyBatchToForm(mostRecentBatch, currentValues, setValues);
    },
    [applyBatchToForm, batchStore, settingsStore],
  );

  return applyPreviousValues;
};

export const loadRecentBatches = async (
  settingsStore: RootStore['settingsStore'],
): Promise<RecentSavedBatch[]> => {
  await initializeIndexDB();

  try {
    const recentBatches = await indexDB!.getByKey(RECENT_BATCHES_KEY) || [];
    const cleanedBatches = await cleanupExpiredBatches(recentBatches, settingsStore);

    // Update storage if we cleaned up any batches
    if (cleanedBatches.length !== recentBatches.length) {
      await indexDB!.save(RECENT_BATCHES_KEY, cleanedBatches);
    }

    return cleanedBatches;
  } catch (err) {
    logger.warn('Failed to load recent batches:', err);
    return [];
  }
};

export const getBatchById = async (
  batchId: string,
  settingsStore: RootStore['settingsStore'],
): Promise<RecentSavedBatch | undefined> => {
  const recentBatches = await loadRecentBatches(settingsStore);
  return recentBatches.find(batch => batch.id === batchId);
};

export const getMostRecentBatch = async (
  settingsStore: RootStore['settingsStore'],
): Promise<RecentSavedBatch | null> => {
  const recentBatches = await loadRecentBatches(settingsStore);
  return recentBatches.length > 0 ? recentBatches[0] : null;
};

export const useApplyBatchValues = () => {
  const applyBatchToForm = useApplyBatchToForm();
  const { batchStore, settingsStore } = useStores();

  const applyBatchValues = useCallback(
    async (
      batchId: string,
      currentValues: BatchValues,
      setValues: (values: any) => void,
    ): Promise<any | false> => {
      const selectedBatch = await getBatchById(batchId, settingsStore);

      if (!selectedBatch) {
        batchStore.showWarning('Selected batch not found or has expired.');
        return false;
      }

      return applyBatchToForm(selectedBatch, currentValues, setValues);
    },
    [applyBatchToForm, batchStore, settingsStore],
  );

  return applyBatchValues;
};

export const useApplyDryBatchValues = () => {
  const applyBatchToForm = useApplyBatchToForm();
  const { batchStore, settingsStore } = useStores();

  const applyDryBatchValues = useCallback(
    async (
      batchId: string,
      currentValues: BatchValues,
      setValues: (values: any) => void,
    ): Promise<any | false> => {
      const selectedBatch = await getBatchById(batchId, settingsStore);

      if (!selectedBatch) {
        batchStore.showWarning('Selected batch not found or has expired.');
        return false;
      }

      return applyBatchToForm(
        selectedBatch,
        currentValues,
        setValues,
        {
          excludeWater: true,
          excludeQuantity: true,
        },
      );
    },
    [applyBatchToForm, batchStore, settingsStore],
  );

  return applyDryBatchValues;
};
